import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import queryString from 'query-string';
import { createRef, useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useLocation, useSearch } from 'wouter';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import StyledButton from '../buttons/StyledButton';
import DrawsSection from '../items/DrawsSection';
import DrawsWinners from '../partials/DrawsWinners';
import PointsToChancesBanner from '../partials/PointsToChancesBanner';

import styles from '../../../styles/pages/draws-tabs.module.scss';

interface DrawsTabPage {
    page: DrawsPage | null;
    className?: string;
}

function DrawsTabPage({ page, className = null }: DrawsTabPage) {
    const themeClassName = useThemeClassName(styles);

    const { sections: drawsSections = [], description = null } = page || {};

    const route = useUrlGenerator();
    const [locationPathname, navigate] = useLocation();
    const search = useSearch();

    const { draw = null } = useMemo(() => queryString.parse(search) || {}, [search]);
    const currentDrawSection =
        drawsSections !== null && draw !== null
            ? drawsSections.find(({ id }) => `${id}` === `${draw}`) ||
              drawsSections.find(({ draws = [] }) => draws.find((id) => `${id}` === `${draw}`))
            : (drawsSections || [])[0] || null;

    const { id: currentDrawSectionId = null, draws: currentDraws = [] } = currentDrawSection || {};

    const [currentDraw = null] = currentDraws || [];
    const {
        id: currentDrawId = null,
        pointsByParticipation: currentDrawPointsByParticipation = null,
    } = currentDraw || {};

    const onTabClick = useCallback(
        (id) => {
            navigate(`${locationPathname}?draw=${id}`);
        },
        [locationPathname],
    );

    const onAccountClick = useCallback(() => {
        navigate(route('account'));
    }, [navigate, route]);

    // React-Transition-Group
    const rtgNodes = useRef({});
    if ((rtgNodes.current[currentDrawId] || null) === null) {
        rtgNodes.current[currentDrawId] = createRef();
    }
    const rtgClassNames = useMemo(
        () => ({
            enterActive: styles.enterActive,
            enterDone: styles.enterDone,
            exitActive: styles.exitActive,
            exitDone: styles.exitDone,
        }),
        [],
    );

    const hasTabs = drawsSections !== null && drawsSections.length > 1;

    const hasPointsEntry = currentDrawPointsByParticipation !== null;

    const draws = useMemo(
        () => drawsSections.reduce((all, { draws: sectionDraws }) => [...all, ...sectionDraws], []),
        [drawsSections],
    );

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            {/* {!hasTabs ? <PageTitle title={pageTitle} className={styles.title} /> : null} */}
            {hasPointsEntry && currentDrawPointsByParticipation > 1 ? (
                <PointsToChancesBanner
                    className={classNames([styles.pointsToChancesBanner])}
                    points={currentDrawPointsByParticipation}
                    description={description}
                />
            ) : null}
            {hasTabs ? (
                <div className={styles.drawTabsContainer}>
                    <div className={styles.drawTabs}>
                        {(drawsSections || []).map(
                            ({ id, title, draws: sectionDraws = [] }, drawIndex) => (
                                <Button
                                    key={`draw-${drawIndex + 1}`}
                                    className={classNames([
                                        styles.drawTab,
                                        { [styles.active]: `${id}` === `${currentDrawSectionId}` },
                                    ])}
                                    onClick={() => {
                                        onTabClick(id);
                                    }}
                                >
                                    {title || (draws.length > 0 ? sectionDraws[0].title : null)}
                                </Button>
                            ),
                        )}
                    </div>
                </div>
            ) : null}

            <TransitionGroup className={styles.selectedTabContainer}>
                {currentDrawSection !== null ? (
                    <CSSTransition
                        key={currentDrawId}
                        classNames={rtgClassNames}
                        nodeRef={rtgNodes.current[currentDrawId]}
                        timeout={250}
                    >
                        <div className={styles.selectedTab} ref={rtgNodes.current[currentDrawId]}>
                            <DrawsSection
                                section={currentDrawSection}
                                className={styles.section}
                                prizeClassName={styles.prize}
                                prizeCardClassName={styles.prizeCard}
                            />
                        </div>
                    </CSSTransition>
                ) : null}
            </TransitionGroup>
            <DrawsWinners draws={draws} className={styles.winners} />
            <div className={classNames([styles.winningChances])}>
                <StyledButton
                    className={classNames([styles.winningChancesButton])}
                    colored
                    onClick={() => {
                        onAccountClick();
                    }}
                >
                    <FormattedMessage
                        defaultMessage="See my winning chances"
                        description="Draws page winning chances button"
                    />
                </StyledButton>
            </div>
        </div>
    );
}

export default DrawsTabPage;
